"use client";

import { cn } from "@/src/utils/functions";
import React, { FormEventHandler, forwardRef, use, useEffect, useId, useImperativeHandle, useRef, useState } from "react";

interface FloatingInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    autocomplete?: string;
    autofocus?: boolean;
    className?: string;
    hasError?: boolean;
    errorMessage?: string;
    id?: string;
    name?: string;
    type?: string;
    variant?: "sm" | "md" | "lg" | undefined;
    placeholder?:string;
    placeholderClassName?:string;
    value?: any;
    required?: boolean;
    onChange?: FormEventHandler<HTMLInputElement>;
    onBlur?: FormEventHandler<HTMLInputElement>;
}

export interface InputRef {
    focus: () => void;
    blur: () => void;
    select: () => void;
    value: string;
}

const FloatingInput = forwardRef<InputRef, FloatingInputProps>((props: FloatingInputProps, ref) => {

    const { autocomplete, autofocus, className, errorMessage, hasError=false, id, name="", placeholder = "Enter input", placeholderClassName, required, type="text", value, variant="lg", onBlur, onChange } = props;

    const input:any = useRef<HTMLInputElement>(null);
    const [active, setActive] = useState(false);

    const inputId = id || useId();

    useImperativeHandle(ref, () => ({
        focus: () => input.current?.focus(),
        blur: () => input.current?.blur(),
        select: () => input.current?.select(),
        get value() {
            return input.current?.value || "";
        },
        set value(newValue: string) {
            if (input.current) {
                input.current.value = newValue
            }
        }
    }), []);

    useEffect(() => {
        
        if (input.current.hasAttribute("autofocus")) {
            input.current.focus();
        }

        const handleBlur = (evt:any) => {
            if (!!evt.target.value) {
                setActive(true);
            } else {
                setActive(false);
            }
        }

        input.current.addEventListener("blur", handleBlur);

        // return () => {
        //     input.current.removeEventListener("blur", handleBlur);
        // }
    }, []);

    useEffect(() => {
        if (!!value) {
            setActive(true);
        } else {
            setActive(false);
        }
    }, [value])

    const hasValue = () => {
        return active ? "has-value" : "";
    }

    const setHasErrorClass = () => {
        return hasError? "has-error" : "";
    }

    return (
        <div className="flex flex-col gap-y-2">
            <div className={`peer relative w-full p-0 border-none group ${variant} ${hasValue()} ${setHasErrorClass()}`}>
                <input
                    ref={input}
                    id={inputId}
                    name={name}
                    type={type}
                    autoComplete={autocomplete}
                    autoFocus={autofocus ?? false}
                    required={required ?? false}
                    className={
                        cn (
                            `w-full peer`, 
                            `h-15.5 group-[.sm]:h-10 group-[.md]:h-13`,
                            `focus:outline-none dark:text-gray-300 ring-2 ring-gray-200 dark:ring-gray-700 dark:bg-gray-900 
                            group-[.has-error]:focus:ring-red-500 group-[.has-error]:dark:focus:ring-red-600 group-[.has-error]:ring-red-500 
                            group-[.has-value.has-error]:ring-red-500 focus:border-sky-500 dark:focus:border-sky-600 group-[.has-value]:border-sky-500 
                            focus:ring-sky-500 group-[.has-value]:ring-sky-500 focus:ring-2 rounded-[5px] placeholder:text-slate-400 font-light 
                            transition-all duration-300`, 
                            `text-[16px] group-[.md]:text-[14px]`,
                            `px-5 focus:pt-3 group-[.has-value]:pt-3 group-[.has-error]:pt-3 `,
                            `group-[.md]:focus:pt-2 group-[.md]:group-[.has-value]:pt-2 group-[.md]:group-[.has-error]:pt-2`,
                            className
                        )
                    }
                    value={value}
                    onChange={onChange} 
                    onBlur={onBlur}
                />

                <label 
                    htmlFor={inputId} 
                    className={
                        cn(
                            `absolute pl-5 text-gray-300 dark:text-gray-600 font-normal peer-focus:text-sky-500 cursor-text transition-all ease-in-out delay-75 
                            duration-200 group-[.has-value]:text-sky-500 group-[.has-error]:text-red-500 group-[.has-error]:peer-focus:text-red-500 
                            group-[.has-value.has-error]:text-red-500 peer-autofill:text-sky-500 peer-focus:font-medium group-[.has-value]:font-medium select-none`, 
                            `peer-focus:text-[12px] group-[.has-value]:text-[12px] peer-autofill:text-[12px]`,
                            `left-0 top-4.5 group-[.md]:top-3.5`, 
                            `peer-focus:top-1.5 group-[.has-value]:top-1.5 group-[.md]:group-[.has-value]:top-1.5 peer-autofill:top-1.5`,
                            // `peer-focus:text-[12px] group-[.md]:group-[.has-value]:text-[10px] peer-autofill:text-[12px]`,
                            placeholderClassName
                        )
                    }
                >
                    { placeholder }
                </label>
            </div>

            <span className="hidden peer-[.has-error]:block text-[14px] text-red-500 font-medium">{ errorMessage }</span>
        </div>
    )
});

FloatingInput.displayName = "FloatingInput";

export default FloatingInput;